import { createStart, createMiddleware } from "@tanstack/react-start";

import { renderErrorPage } from "./lib/error-page";
import { attachSupabaseAuth } from "@/integrations/supabase/auth-attacher";
import { isGone, shouldBeNoindex } from "@/lib/seo-policy";
import { STATES } from "@/data/states";

const STATE_SLUGS = new Set(STATES.map((s) => s.slug));

/**
 * Edge cache policy per public page family. Archive/history pages barely change,
 * dated result pages are immutable once drawn, and jackpot-bearing hubs get a
 * short window so numbers stay fresh.
 */
function publicPageEdgeTtl(pathname: string): { sMaxAge: number; swr: number } | null {
  const p = pathname.replace(/\/+$/, "") || "/";
  const segs = p.split("/").filter(Boolean);
  const datedResult = /\/results\/\d{4}-\d{2}-\d{2}$/.test(p);
  const isStateScoped = segs.length >= 2 && STATE_SLUGS.has(segs[0]);

  if (datedResult) return { sMaxAge: 1800, swr: 86400 };
  if (/\/history$/.test(p)) return { sMaxAge: 900, swr: 3600 };
  if (p === "/states" || /^\/states\//.test(p)) return { sMaxAge: 300, swr: 1800 };
  if (/^\/games(?:\/|$)/.test(p) || /^\/results(?:\/|$)/.test(p)) {
    return { sMaxAge: 300, swr: 1800 };
  }
  if (isStateScoped) return { sMaxAge: 300, swr: 1800 };
  if (p === "/") return { sMaxAge: 120, swr: 600 };
  return null;
}

// Permanently removed/replaced URLs answer 410 Gone so Google drops them fast,
// and every robots.txt-blocked path also gets an X-Robots-Tag: noindex header
// so headers can never disagree with robots.txt or sitemap membership.
const indexingPolicyMiddleware = createMiddleware().server(async (ctx: any) => {
  const { next, request } = ctx;
  let pathname = "";
  try {
    pathname = new URL((request as Request).url).pathname;
  } catch {
    /* noop */
  }

  if (pathname && isGone(pathname)) {
    return new Response(
      "410 Gone — this page has been permanently removed. Visit https://usalotteryclub.com/ for current results.",
      {
        status: 410,
        headers: {
          "content-type": "text/plain; charset=utf-8",
          "x-robots-tag": "noindex, nofollow",
          "cache-control": "public, max-age=3600",
        },
      },
    );
  }

  const result = await next();
  const res = (result as { response?: Response }).response;
  if (!res || !(res instanceof Response)) return result;
  if (!pathname || !shouldBeNoindex(pathname)) return result;
  const headers = new Headers(res.headers);
  headers.set("X-Robots-Tag", "noindex, nofollow");
  return new Response(res.body, { status: res.status, statusText: res.statusText, headers });
});

const errorMiddleware = createMiddleware().server(async ({ next }) => {
  try {
    return await next();
  } catch (error) {
    if (error != null && typeof error === "object" && "statusCode" in error) {
      throw error;
    }
    console.error(error);
    return new Response(renderErrorPage(), {
      status: 500,
      headers: { "content-type": "text/html; charset=utf-8" },
    });
  }
});

// Hardened browser security headers applied to every server response.
// CSP is intentionally in Report-Only mode so we can observe violations
// without breaking the app; flip to `Content-Security-Policy` once verified.
const securityHeadersMiddleware = createMiddleware().server(async (ctx: any) => {
  const { next, request } = ctx;
  const result = await next();
  const res = (result as { response?: Response }).response;
  if (!res || !(res instanceof Response)) return result;

  const csp = [
    "default-src 'self'",
    "base-uri 'self'",
    "frame-ancestors 'self'",
    "form-action 'self'",
    "object-src 'none'",
    "img-src 'self' data: blob: https:",
    "font-src 'self' data: https:",
    "style-src 'self' 'unsafe-inline' https:",
    "script-src 'self' 'unsafe-inline' 'unsafe-eval' https:",
    "connect-src 'self' https: wss:",
    "media-src 'self' https: data: blob:",
    "worker-src 'self' blob:",
    "upgrade-insecure-requests",
  ].join("; ");

  const headers = new Headers(res.headers);
  headers.set("Strict-Transport-Security", "max-age=63072000; includeSubDomains; preload");
  headers.set("X-Content-Type-Options", "nosniff");
  headers.set("X-Frame-Options", "SAMEORIGIN");
  headers.set("Referrer-Policy", "strict-origin-when-cross-origin");
  headers.set(
    "Permissions-Policy",
    "camera=(), microphone=(), geolocation=(), payment=(), usb=(), interest-cohort=()",
  );
  headers.set("Cross-Origin-Opener-Policy", "same-origin");
  headers.set("X-DNS-Prefetch-Control", "off");
  // Single-locale (US English) site — keeps hreflang/lang/header consistent.
  if (!headers.has("Content-Language")) headers.set("Content-Language", "en-US");
  if (!headers.has("Content-Security-Policy")) {
    headers.set("Content-Security-Policy-Report-Only", csp);
  }

  // Caching policy for static assets.
  // - Hashed bundles under /assets/ and /_build/ are content-addressed by Vite,
  //   so they're safe to cache immutably for a year.
  // - Non-hashed files served from /public/ (favicons, og-image, /game-logos/*)
  //   live at stable URLs and MUST remain revalidatable so replacements roll
  //   out to returning visitors. Give them a short cache with revalidation.
  try {
    const reqUrl = (request as Request | undefined)?.url;
    if (reqUrl && !headers.has("Cache-Control")) {
      const p = new URL(reqUrl).pathname;
      const isHashedAsset = p.startsWith("/assets/") || p.startsWith("/_build/");
      const isStaticFile =
        /\.(?:js|css|woff2?|ttf|otf|png|jpe?g|webp|avif|gif|svg|ico)$/.test(p);
      if (isHashedAsset) {
        headers.set("Cache-Control", "public, max-age=31536000, immutable");
      } else if (isStaticFile) {
        headers.set("Cache-Control", "public, max-age=3600, must-revalidate");
      } else {
        // Public, non-personalized content pages. Served from the edge/CDN with
        // background revalidation so repeat visitors and crawlers never re-run
        // SSR or the underlying data reads. TTL scales with how often the page
        // can actually change.
        const edgeTtl = publicPageEdgeTtl(p);
        if (edgeTtl) {
          headers.set(
            "Cache-Control",
            `public, max-age=0, s-maxage=${edgeTtl.sMaxAge}, stale-while-revalidate=${edgeTtl.swr}`,
          );
        }
      }
    }
  } catch {
    /* noop */
  }

  return new Response(res.body, {
    status: res.status,
    statusText: res.statusText,
    headers,
  });
});

export const startInstance = createStart(() => ({
  functionMiddleware: [attachSupabaseAuth],
  requestMiddleware: [errorMiddleware, indexingPolicyMiddleware, securityHeadersMiddleware],
}));
